import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  useMediaQuery,
  Drawer,
  Switch,
} from '@mui/material';
import {
  People as PeopleIcon,
  LocalHospital as ClinicsIcon,
  Person as ProvidersIcon,
  Medication as ProductsIcon,
  Folder as ResourcesIcon,
  Assignment as SubmissionsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Menu as MenuIcon,
  SupervisorAccount as AdminIcon,
  Assessment as ReportsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { displayRole } from '../../utils/roleDisplayLabels';
import { SessionTimeoutProvider } from '../../contexts/SessionTimeoutContext';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '../../contexts/UserContext';
import LogoIcon from '../common/MedNecessityLogo';

// Navigation item type
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  dividerAfter?: boolean;
}

const ClientLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1200px)');
  const isDesktop = useMediaQuery('(min-width: 1201px)');
  
  // Auto-collapse on smaller screens, expand on desktop
  const [collapsed, setCollapsed] = useState(!isDesktop);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Update collapsed state when screen size changes
  React.useEffect(() => {
    if (isDesktop) {
      setCollapsed(false);
    } else if (isTablet) {
      setCollapsed(true);
    }
  }, [isDesktop, isTablet]);

  // Get user info
  const { user, setUser } = useUser();
  const roleNorm = (user?.Role || user?.role || '').toLowerCase();
  const isClientAdmin = roleNorm === 'client admin';
  const isClinicAdmin = roleNorm === 'clinic admin';
  const isClinicUser = roleNorm === 'clinic user';
  const isClinicScoped = isClinicAdmin || isClinicUser;
  const userName = user?.Name || user?.Username || 'User';
  const userInitial = userName.charAt(0).toUpperCase();

  // Navigation items — clinic-scoped users don't see Clinics
  const navItems: NavItem[] = [
    { id: 'patients', label: 'Patients', icon: <PeopleIcon />, path: '/client/patients' },
    ...(!isClinicScoped ? [{ id: 'clinics', label: 'Clinics', icon: <ClinicsIcon />, path: '/client/clinics' }] : []),
    { id: 'providers', label: 'Providers', icon: <ProvidersIcon />, path: '/client/providers' },
    { id: 'products', label: 'Products', icon: <ProductsIcon />, path: '/client/products', dividerAfter: true },
    { id: 'submissions', label: 'Submissions', icon: <SubmissionsIcon />, path: '/client/crm' },
    { id: 'resources', label: 'Resources', icon: <ResourcesIcon />, path: '/client/resources' },
  ];

  // Admin-section items
  const adminItems: NavItem[] = isClientAdmin
    ? [
        { id: 'admin-users', label: 'User Management', icon: <AdminIcon />, path: '/client-admin/users' },
        { id: 'admin-reports', label: 'Reports', icon: <ReportsIcon />, path: '/client-admin/reports' },
      ]
    : isClinicAdmin
    ? [{ id: 'clinic-admin-users', label: 'User Management', icon: <AdminIcon />, path: '/clinic-admin/users' }]
    : [];

  const handleLogout = async () => {
    try {
      await api.post('/api/logout', {}, { withCredentials: true });
      setUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  const isActivePath = (path: string) => {
    // Check for exact match or if current path starts with the nav path
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const navWidth = collapsed ? 64 : 240;

  const NavContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'var(--surface-carbon)',
        borderRight: '1px solid var(--surface-jet)',
        width: isMobile ? 240 : navWidth,
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Header / Logo Area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          p: 'var(--spacing-md)',
          borderBottom: '1px solid var(--surface-jet)',
          minHeight: 64,
        }}
      >
        {(!collapsed || isMobile) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            <LogoIcon size={28} color="var(--accent-primary)" />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.9375rem',
                color: 'var(--text-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              MedNecessity
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              color: 'var(--text-secondary)',
              '&:hover': { bgcolor: 'var(--surface-jet)' },
              p: 'var(--spacing-xs)',
            }}
          >
            {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Main Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 'var(--spacing-sm)' }}>
        <List disablePadding>
          {navItems.map((item) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding sx={{ px: 'var(--spacing-sm)' }}>
                <Tooltip title={collapsed && !isMobile ? item.label : ''} placement="right">
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActivePath(item.path)}
                    sx={{
                      borderRadius: 'var(--radius-md)',
                      mb: 'var(--spacing-xs)',
                      py: 'var(--spacing-sm)',
                      px: collapsed && !isMobile ? 'var(--spacing-sm)' : 'var(--spacing-md)',
                      justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                      minHeight: 40,
                      bgcolor: isActivePath(item.path) ? 'var(--surface-jet)' : 'transparent',
                      borderLeft: isActivePath(item.path) ? '2px solid var(--accent-primary)' : '2px solid transparent',
                      '&:hover': {
                        bgcolor: 'var(--surface-jet)',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'var(--surface-jet)',
                        '&:hover': {
                          bgcolor: 'var(--surface-jet)',
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed && !isMobile ? 0 : 36,
                        color: isActivePath(item.path) ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {(!collapsed || isMobile) && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActivePath(item.path) ? 600 : 500,
                          color: isActivePath(item.path) ? 'var(--text-primary)' : 'var(--text-secondary)',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
              {item.dividerAfter && (
                <Divider sx={{ my: 'var(--spacing-sm)', mx: 'var(--spacing-md)', borderColor: 'var(--surface-jet)' }} />
              )}
            </React.Fragment>
          ))}

          {/* Admin Section */}
          {adminItems.length > 0 && (
            <>
              <Divider sx={{ my: 'var(--spacing-sm)', mx: 'var(--spacing-md)', borderColor: 'var(--surface-jet)' }} />
              {(!collapsed || isMobile) && (
                <Typography
                  sx={{
                    px: 'var(--spacing-lg)',
                    py: 'var(--spacing-xs)',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Admin
                </Typography>
              )}
              {adminItems.map((item) => (
                <ListItem key={item.id} disablePadding sx={{ px: 'var(--spacing-sm)' }}>
                  <Tooltip title={collapsed && !isMobile ? item.label : ''} placement="right">
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      selected={isActivePath(item.path)}
                      sx={{
                        borderRadius: 'var(--radius-md)',
                        mb: 'var(--spacing-xs)',
                        py: 'var(--spacing-sm)',
                        px: collapsed && !isMobile ? 'var(--spacing-sm)' : 'var(--spacing-md)',
                        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                        minHeight: 40,
                        bgcolor: isActivePath(item.path) ? 'var(--surface-jet)' : 'transparent',
                        borderLeft: isActivePath(item.path) ? '2px solid var(--accent-primary)' : '2px solid transparent',
                        '&:hover': {
                          bgcolor: 'var(--surface-jet)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed && !isMobile ? 0 : 36,
                          color: isActivePath(item.path) ? 'var(--accent-primary)' : 'var(--text-secondary)',
                          justifyContent: 'center',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {(!collapsed || isMobile) && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: isActivePath(item.path) ? 600 : 500,
                            color: isActivePath(item.path) ? 'var(--text-primary)' : 'var(--text-secondary)',
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </>
          )}
        </List>
      </Box>

      {/* Footer / User Area */}
      <Box
        sx={{
          borderTop: '1px solid var(--surface-jet)',
          p: 'var(--spacing-sm)',
        }}
      >
        {/* Theme Toggle */}
        <Tooltip title={collapsed && !isMobile ? (isDarkMode ? 'Light Mode' : 'Dark Mode') : ''} placement="right">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
              px: collapsed && !isMobile ? 'var(--spacing-sm)' : 'var(--spacing-md)',
              py: 'var(--spacing-sm)',
              borderRadius: 'var(--radius-md)',
              mb: 'var(--spacing-xs)',
              cursor: 'pointer',
              justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
              '&:hover': {
                bgcolor: 'var(--surface-jet)',
              },
            }}
            onClick={toggleTheme}
          >
            {isDarkMode ? (
              <LightModeIcon sx={{ fontSize: 20, color: 'var(--accent-primary)' }} />
            ) : (
              <DarkModeIcon sx={{ fontSize: 20, color: 'var(--text-secondary)' }} />
            )}
            {(!collapsed || isMobile) && (
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--text-secondary)',
                  }}
                >
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </Typography>
                <Switch
                  checked={isDarkMode}
                  onChange={toggleTheme}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--accent-primary)',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--accent-primary)',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: 'var(--surface-jet)',
                    },
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* Settings */}
        <ListItem disablePadding sx={{ mb: 'var(--spacing-xs)' }}>
          <Tooltip title={collapsed && !isMobile ? 'Settings' : ''} placement="right">
            <ListItemButton
              onClick={() => handleNavigation('/client/settings')}
              selected={isActivePath('/client/settings')}
              sx={{
                borderRadius: 'var(--radius-md)',
                py: 'var(--spacing-sm)',
                px: collapsed && !isMobile ? 'var(--spacing-sm)' : 'var(--spacing-md)',
                justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                minHeight: 40,
                bgcolor: isActivePath('/client/settings') ? 'var(--surface-jet)' : 'transparent',
                '&:hover': {
                  bgcolor: 'var(--surface-jet)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed && !isMobile ? 0 : 36,
                  color: isActivePath('/client/settings') ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  justifyContent: 'center',
                }}
              >
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {(!collapsed || isMobile) && (
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--text-secondary)',
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* User Profile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)',
            p: 'var(--spacing-sm)',
            borderRadius: 'var(--radius-md)',
            bgcolor: 'var(--surface-jet)',
            cursor: 'pointer',
            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
            '&:hover': {
              bgcolor: 'rgba(111, 159, 149, 0.1)',
            },
          }}
          onClick={handleLogout}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: 'var(--accent-primary)',
              color: 'var(--surface-onyx)',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            {userInitial}
          </Avatar>
          {(!collapsed || isMobile) && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: 'var(--text-muted)',
                  }}
                >
                  {displayRole(user?.Role || 'Client')}
                </Typography>
              </Box>
              <LogoutIcon sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <SessionTimeoutProvider>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          bgcolor: 'var(--surface-onyx)',
        }}
      >
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{
              position: 'fixed',
              top: 'var(--spacing-md)',
              left: 'var(--spacing-md)',
              zIndex: 1200,
              bgcolor: 'var(--surface-carbon)',
              color: 'var(--text-primary)',
              border: '1px solid var(--surface-jet)',
              '&:hover': { bgcolor: 'var(--surface-jet)' },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100vh',
              zIndex: 1100,
            }}
          >
            <NavContent />
          </Box>
        )}

        {/* Mobile Drawer */}
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              bgcolor: 'transparent',
              border: 'none',
            },
          }}
        >
          <NavContent />
        </Drawer>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            overflow: 'auto',
            ml: isMobile ? 0 : `${navWidth}px`,
            transition: 'margin-left 0.2s ease',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </SessionTimeoutProvider>
  );
};

export default ClientLayout;

